import { asLink } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';

import { cn } from '@april9au/react-common/utils';

import type { Content } from '@prismicio/client';
import type { JSXMapSerializer } from '@prismicio/react';
import { Icon } from '@/components/ui/icon';
import { Media } from '@/components/ui/media';
import { useStrings } from '@/i18n/use-strings';
import { isBlank, isExternalHref, isHeadingLike, wholeNodeLink } from './rich-text';

function ContentLink({ href, children }: { href: string; children: React.ReactNode }) {
  const external = isExternalHref(href);

  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className="text-surf-blue focus-visible:ring-surf-blue/40 rounded font-semibold [overflow-wrap:break-word] underline decoration-from-font underline-offset-2 hover:decoration-2 focus-visible:ring-4 focus-visible:outline-none"
    >
      {children}
    </a>
  );
}

/** One resource, as a card: name, an affordance, and the whole row is the target. */
function LinkCard({ href, label, newTabLabel }: { href: string; label: string; newTabLabel: string }) {
  const external = isExternalHref(href);

  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className={cn(
        'group border-surf-gray text-surf-dark-blue flex min-h-14 items-center gap-4 rounded-2xl border-2 bg-white px-4 py-3',
        'transition-[border-color,scale] duration-150 active:scale-96 motion-reduce:transition-none',
        'hover:border-surf-blue/40 focus-visible:border-surf-blue focus-visible:ring-surf-blue/40 focus-visible:ring-4 focus-visible:outline-none',
      )}
    >
      <span className="min-w-0 flex-1 text-base leading-snug font-bold text-balance">
        {label}
        {external ? <span className="sr-only"> ({newTabLabel})</span> : null}
      </span>
      <Icon name="passport/external-link" aria-hidden className="text-surf-blue size-5 shrink-0 rtl:-scale-x-100" />
    </a>
  );
}

/** Headings descend by step; adjacent small levels share a size and differ in weight. */
const headingClass: Record<'h2' | 'h3' | 'h4', string> = {
  h2: 'font-bebas text-surf-dark-blue mt-8 text-2xl leading-[1.1] tracking-wide text-balance uppercase first:mt-0 sm:text-3xl',
  h3: 'text-surf-dark-blue mt-6 text-lg leading-snug font-bold text-balance first:mt-0',
  h4: 'text-surf-dark-blue mt-5 text-base leading-snug font-bold text-balance first:mt-0',
};

/**
 * The `general_content` slice: the whole body of every content page.
 *
 * Two flags restyle it without touching the documents, which are read-only
 * production content:
 *
 * - `lede` renders the first real paragraph as a standfirst (About);
 * - `linkCards` renders whole-node link headings as cards (Resources).
 */
export default function GeneralContent({
  slice,
  lede = false,
  linkCards = false,
}: {
  slice: Content.GeneralContentSlice;
  lede?: boolean;
  linkCards?: boolean;
}) {
  const { content } = useStrings();

  const components: JSXMapSerializer = {
    paragraph: ({ node, children, key }) => {
      if (isBlank(node)) {
        return <span key={key} hidden />;
      }

      if (isHeadingLike(node)) {
        return (
          <h2 key={key} className={headingClass.h2}>
            {node.text.trim()}
          </h2>
        );
      }

      return (
        // `pre-line` keeps the soft line breaks editors rely on for addresses
        // and contact blocks, which are otherwise collapsed into one line.
        <p key={key} className="mt-4 text-base leading-[1.6] whitespace-pre-line first:mt-0">
          {children}
        </p>
      );
    },

    heading2: ({ children, key }) => (
      <h2 key={key} className={headingClass.h2}>
        {children}
      </h2>
    ),
    heading3: ({ children, key }) => (
      <h3 key={key} className={headingClass.h3}>
        {children}
      </h3>
    ),
    heading4: ({ children, key }) => (
      <h4 key={key} className={headingClass.h4}>
        {children}
      </h4>
    ),
    // Levels 5 and 6 are the same visual step as 4; the element keeps the
    // outline honest. On Resources they are the link cards.
    heading5: ({ node, children, key }) => {
      const link = linkCards ? wholeNodeLink(node) : null;

      return link ? (
        <LinkCard key={key} href={link.href} label={link.label} newTabLabel={content.content_new_tab} />
      ) : (
        <h4 key={key} className={headingClass.h4}>
          {children}
        </h4>
      );
    },
    heading6: ({ node, children, key }) => {
      const link = linkCards ? wholeNodeLink(node) : null;

      return link ? (
        <LinkCard key={key} href={link.href} label={link.label} newTabLabel={content.content_new_tab} />
      ) : (
        <h4 key={key} className={headingClass.h4}>
          {children}
        </h4>
      );
    },

    strong: ({ children, key }) => (
      <strong key={key} className="font-bold">
        {children}
      </strong>
    ),

    list: ({ children, key }) => (
      <ul key={key} className="mt-4 space-y-2 ps-5 text-base leading-[1.6] [list-style:disc] first:mt-0">
        {children}
      </ul>
    ),
    oList: ({ children, key }) => (
      <ol key={key} className="mt-4 space-y-2 ps-5 text-base leading-[1.6] [list-style:decimal] first:mt-0">
        {children}
      </ol>
    ),
    listItem: ({ children, key }) => <li key={key}>{children}</li>,
    oListItem: ({ children, key }) => <li key={key}>{children}</li>,

    hyperlink: ({ node, children, key }) => {
      const href = asLink(node.data)?.trim();

      return href ? (
        <ContentLink key={key} href={href}>
          {children}
        </ContentLink>
      ) : (
        <span key={key}>{children}</span>
      );
    },

    // Intrinsic size from Prismic, so the page reserves the space before the
    // file arrives and nothing below it moves; `Media` fades it over a tint
    // rather than flashing it in mid-paragraph.
    image: ({ node, key }) => (
      <Media
        key={key}
        src={node.url}
        alt={node.alt ?? ''}
        width={node.dimensions.width}
        height={node.dimensions.height}
        placeholder="var(--color-surf-gray)"
        className="mt-6 h-auto w-full rounded-2xl outline outline-black/10 first:mt-0"
      />
    ),

    // No content page embeds anything today, and the CSP does not allow an
    // arbitrary frame source, so an embed degrades to its own link.
    embed: ({ node, key }) =>
      node.oembed.embed_url ? (
        <p key={key} className="mt-4">
          <ContentLink href={node.oembed.embed_url}>{node.oembed.title ?? node.oembed.embed_url}</ContentLink>
        </p>
      ) : (
        <span key={key} hidden />
      ),
  };

  return (
    <div
      className={cn(
        // 68ch keeps long-form copy inside a comfortable measure; the cards on
        // Resources are their own rhythm.
        'text-surf-dark-blue max-w-[68ch] text-pretty',
        linkCards && 'space-y-2',
        lede &&
          '[&>p:first-of-type]:text-surf-dark-blue [&>p:first-of-type]:text-lg [&>p:first-of-type]:leading-[1.55] [&>p:first-of-type]:font-medium',
      )}
    >
      <PrismicRichText field={slice.primary.content} components={components} />
    </div>
  );
}
